import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Row,
  Col,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import DuplicatePreviewItem from './PreviewItem';

import { formatBytes } from '../../../helpers/Files';

import ErrorBoundary from '../../ErrorBoundary';
import Loading from '../../Loading';

const DuplicatePreview = ({
  duplicate,
  mode,
  selectedFiles,
  loading,
  hiding,
  openFileMergeModal,
  handleSwitchSelected,
  handleUnselectAll,
  handleArchiveFiles,
  handleDeleteFiles,
  handleSwitchIgnore,
}) => {
  const intl = useIntl();

  return (
    <ErrorBoundary>
      <Card>
        <CardHeader>
          <Row>
            <Col>
              {intl.formatMessage({ defaultMessage: 'Preview' })}
              {(duplicate && duplicate.duplicates_size > 0) &&
              (
                <>
                  {' '}
                  {intl.formatMessage(
                    { defaultMessage: '(save up to {size})' },
                    { size: formatBytes(duplicate.duplicates_size) },
                  )}
                </>
              )}
            </Col>
            {duplicate && (
            <Col className="right">
              {hiding ? (
                <i className="far fa-spinner fa-spin" />
              ) : (
                <Fragment>
                  {duplicate.hidden ? (
                    <a
                      id="switch-ignore"
                      href="#unignore"
                      onClick={handleSwitchIgnore}
                    >
                      <i className="far fa-eye" />
                    </a>
                  ) : (
                    <a
                      id="switch-ignore"
                      href="#ignore"
                      onClick={handleSwitchIgnore}
                    >
                      <i className="far fa-eye-slash" />
                    </a>
                  )}
                  <UncontrolledTooltip placement="bottom" target="switch-ignore">
                    {duplicate.hidden ?
                      intl.formatMessage({ defaultMessage: 'Unignore' }) :
                      intl.formatMessage({ defaultMessage: 'Ignore' })}
                  </UncontrolledTooltip>
                </Fragment>
              )}
            </Col>
            )}
          </Row>
        </CardHeader>
        <CardBody>
          {(loading || !duplicate) ? (
            <Loading />
          ) : (
            <ul className="duplicated-previews-list">
              {duplicate.files.map(f => (
                <DuplicatePreviewItem
                  key={f.uuid}
                  file={f}
                  selected={selectedFiles.includes(f.uuid)}
                  handleSwitchSelected={handleSwitchSelected}
                  handleArchiveFiles={handleArchiveFiles}
                  handleDeleteFiles={handleDeleteFiles}
                />
              ))}
            </ul>
          )}
        </CardBody>
        {!loading && (
        <CardFooter>
          <div className="duplicated-footer-wrapper">
            <div className="files-selected">
              {selectedFiles.length > 0 && (
                <Fragment>
                  <span className="files-selected-num">
                    {intl.formatMessage({ defaultMessage: 'Selected: {fileCount} {fileCount, plural, one {file} other {files}}' }, { fileCount: selectedFiles.length })}
                  </span>
                  <span>
                    <button
                      type="button"
                      className="files-unselect-all btn btn-link"
                      onClick={handleUnselectAll}
                    >
                      {intl.formatMessage({ defaultMessage: 'Unselect all' })}
                    </button>
                  </span>
                </Fragment>
              )}
            </div>
            <div className="buttons-group">
              <Button
                color="secondary"
                id={duplicate.uuid}
                onClick={() => {
                  handleDeleteFiles(selectedFiles);
                }}
                disabled={selectedFiles.length === 0}
              >
                {intl.formatMessage({ defaultMessage: 'Delete' })}
                {selectedFiles.length > 0 && (
                  <>
                    {' '}
                    {intl.formatMessage({ defaultMessage: '{fileCount} {fileCount, plural, one {file} other {files}}' }, { fileCount: selectedFiles.length })}
                  </>
                )}
              </Button>
              <Button
                color="secondary"
                id={duplicate.uuid}
                onClick={() => {
                  handleArchiveFiles(selectedFiles);
                }}
                disabled={selectedFiles.length === 0}
              >
                {intl.formatMessage({ defaultMessage: 'Archive' })}
                {selectedFiles.length > 0 && (
                  <>
                    {' '}
                    {intl.formatMessage({ defaultMessage: '{fileCount} {fileCount, plural, one {file} other {files}}' }, { fileCount: selectedFiles.length })}
                  </>
                )}
              </Button>
              {mode !== 'similar_images' && (
                <Button
                  color="primary"
                  id={duplicate.uuid}
                  onClick={openFileMergeModal}
                  disabled={selectedFiles.length < 2}
                >
                  {intl.formatMessage({ defaultMessage: 'Merge' })}
                  {selectedFiles.length > 1 && (
                    <>
                      {' '}
                      {intl.formatMessage({ defaultMessage: '{fileCount} {fileCount, plural, one {file} other {files}}' }, { fileCount: selectedFiles.length })}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardFooter>
        )}
      </Card>
    </ErrorBoundary>
  );
};

DuplicatePreview.propTypes = {
  duplicate: PropTypes.object,
  mode: PropTypes.string.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  hiding: PropTypes.bool.isRequired,
  openFileMergeModal: PropTypes.func.isRequired,
  handleSwitchSelected: PropTypes.func.isRequired,
  handleUnselectAll: PropTypes.func.isRequired,
  handleArchiveFiles: PropTypes.func.isRequired,
  handleDeleteFiles: PropTypes.func.isRequired,
  handleSwitchIgnore: PropTypes.func.isRequired,
};

DuplicatePreview.defaultProps = {
  duplicate: null,
};

export default DuplicatePreview;
